/*
* # IVanillaReaderHighlight
*
* This interface defines the data that is used to describe a highlight annotation in the VanillaReader. This data is used
* by for example the highlight annotation layer and the UI.
*
**/
import {
    IVanillaReaderSyncMediaOptions,
    IVanillaReaderViewOptions, VanillaReaderPublicationLandmarkType
} from "../VanillaReader/VanillaReaderModel";
import {
    ContainerContentBlockType,
    IContentBlockData, IPublicationStyleFontSet,
    IPublicationStyleOptions,
    IPublicationStylePalette,
    IReaderPublicationStorageState, IVisibleContentRectData,
    MediaContentBlockType,
    NavigationCollectionType,
    TextContentBlockType
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import {Key} from "../utils/Key.enum";
import {
    IPublicationMetadataData,
    IPublicationMetadataItem
} from "@colibrio/colibrio-reader-framework/colibrio-core-publication-base";
import {IntRange} from "../utils/IntRange";
import {IAttributeData} from "@colibrio/colibrio-reader-framework/colibrio-core-base";

export interface IVanillaReaderHighlightStoreData {
    _id?: string,
    color: string,
    comment: string,
    locator: string,
    selectionText: string,
    publicationId?: string,
    bookSection?: string,
    dateCreated?: number,
    dateUpdated?: number,
}

/*
* # IVanillaReaderBookmark
*
* This interface defines the data that is used to describe a bookmark annotation in the VanillaReader. This data is used
* by for example the bookmark annotation layer and the UI.
*
**/
export interface IVanillaReaderBookmarkStoreData {
    _id?: string,
    locator: string,
    publicationId?: string,
    color?: string,
    text?: string,
    bookSection?: string
    dateCreated?: number
}

/*
* # IVanillaReaderAppState
*
* This interface describes the complete serializable application state including the `IVanillaReaderPublicationData` and
* the `IVanillaReaderViewOptions`. This is all the data that is needed to restore a previous session.
*
* This data is created by the `VanillaReader` class.
*
* ## RELATED TYPES
*
* - IVanillaReaderPublicationData
* - IVanillaReaderViewOptions
*
**/
export interface IVanillaReaderOptionsStoreData {
    _id?: number,
    dateCreated?: number,
    viewOptions?: IVanillaReaderViewOptions,
    syncMediaOptions?: IVanillaReaderSyncMediaOptions,
}

/*
* # IVanillaReaderAppUiSettingsData
*
* ## RESPONSIBILITIES
*
* This interface defines the data that is needed by the application UI to restore its settings from a previous setting.
*
* It is used as a schema in the `IVanillaReaderDataStore` implementations so change this type with caution if offer the
* app as a PWA as it will require migration.
*
**/
export interface IVanillaReaderStoreUiOptionsData {
    _id?: number,
    dateCreated?: number;
    showNarrationControls?: boolean;
    ignoreAspectRatio?: boolean;
    useReducedMotion?: boolean;
    useDarkMode?: boolean;
    activeRendererTypeName?: VanillaReaderStoreRendererNames;
    activeUiPaletteName?: string,
    activePublicationPaletteName?: string;
    activePublicationFontSetName?: string;
    publicationStyleOptions?: IPublicationStyleOptions;
    mediaPlayerPlaybackRate?: number;
    ttsOptions?: IVanillaReaderStoreTtsOptions,
    useAccessibilityMode?: boolean;
    uIActionKeyCommands?: IVanillaReaderStoreUiActionKeyCommand[],
    fontSets?: {
        SansSerif: IPublicationStyleFontSet;
        Serif: IPublicationStyleFontSet;
        EasyReading: IPublicationStyleFontSet;
    },
    palettes?: {
        Default: IVanillaReaderStoreUiColorPalette;
        Dark: IVanillaReaderStoreUiColorPalette;
        ContrastDark: IVanillaReaderStoreUiColorPalette;
        Paperlike: IVanillaReaderStoreUiColorPalette;
    },
    showReadingRuler?: boolean;
    readingRulerOptions?: IVanillaReaderStoreUiReadingRulerOptions;
    showPageNumbers?: boolean;
    scrollOverflowingTables?: boolean;

}

export const enum VanillaReaderStoreRendererNames {
    RESPONSIVE = '__responsive__',
    FLIPBOOK = 'FlipBookRenderer',
    STACK = 'StackRenderer',
    SCROLL = 'SingleDocumentScrollRenderer',
    SWIPE_SINGLE = 'SinglePageSwipeRenderer',
    SWIPE_SPREAD = 'SpreadSwipeRenderer'
}

export interface IVanillaReaderStoreTtsOptions {
    voiceName: string | undefined,
    highlightColor?: string,
    volume?: number,
    rate?: number
}

export interface IVanillaReaderStoreUiActionKeyCommand {
    actionName: VanillaReaderStoreUiActionNames;
    key: string;
    modifiers: Key[];
}

export interface IVanillaReaderStoreUiColorPalette extends IPublicationStylePalette {
    //IPublicationStylePalette
    foregroundDark: string;
    foregroundLight: string;
    backgroundDark: string;
    backgroundLight: string;
    accent: string;

    // IVanillaReaderUiColorPalette extension
    name: string;
    uiIsDark: boolean;
    uiOutlineDark?: string;
    uiOutlineLight?: string;
    uiBackgroundDark?: string;
    uiBackgroundLight?: string;
    uiForegroundDark?: string;
    uiForegroundLight?: string;
}

export const enum VanillaReaderStoreUiActionNames {
    ACTIONS_MENU_OPEN = 'uiaction_actions_menu_open',
    MAIN_MENU_OPEN = 'uiaction_main_menu_open',
    WHERE_AM_I = 'uiaction_where_am_i',
    TOC_OPEN_DIALOG= 'uiaction_toc_open',
    NAVIGATE_NEXT= 'uiaction_navigate_next',
    NAVIGATE_PREVIOUS= 'uiaction_navigate_previous',
    NAVIGATE_NEXT_HEADING= 'uiaction_navigate_next_heading',
    NAVIGATE_PREVIOUS_HEADING= 'uiaction_navigate_previous_heading',
    HIGHLIGHT_OPEN_DIALOG_ALL = 'uiaction_highlight_open_dialog_all',
    HIGHLIGHT_OPEN_DIALOG_ACTIVE = 'uiaction_highlight_open_dialog_active',
    HIGHLIGHT_SELECTION = 'uiaction_highlight_selection',
    HIGHLIGHT_CLIPBOARD = 'uiaction_highlight_clipboard',
    HIGHLIGHT_READING_POSITION = 'uiaction_highlight_reading_position',
    HIGHLIGHT_ACTIVE_CONTENT = 'uiaction_highlight_active_content',
    BOOKMARK_SELECTION = 'uiaction_bookmark_selection',
    BOOKMARK_CLIPBOARD = 'uiaction_bookmark_clipboard',
    BOOKMARK_READING_POSITION = 'uiaction_bookmark_reading_position',
    BOOKMARK_OPEN_DIALOG_ALL = 'uiaction_bookmark_open_dialog_all',
    BOOKMARK_OPEN_DIALOG_ACTIVE = 'uiaction_bookmark_open_dialog_active',
    SEARCH_DIALOG_OPEN = 'uiaction_search_open_dialog_active',
    SEARCH_CLEAR = 'uiaction_search_clear',
    MEDIA_PLAYBACK_START = 'uiaction_media_playback_start',
    MEDIA_PLAYBACK_STOP = 'uiaction_media_playback_stop',
    MEDIA_PLAYBACK_SKIP_FORWARD = 'uiaction_media_playback_skip_forward',
    MEDIA_PLAYBACK_SKIP_BACKWARD = 'uiaction_media_playback_skip_backward',
    MEDIA_PLAYBACK_ESCAPE_SEGMENT = 'uiaction_media_playback_escape_segment',
    MEDIA_PLAYBACK_ESCAPE_ALL = 'uiaction_media_playback_escape_all',
    HELP_DIALOG_OPEN = 'uiaction_help_dialog_open',
    ACCESSIBILITY_MODE_ENTER = 'uiaction_accessibility_mode_enter',
    ACCESSIBILITY_MODE_EXIT = 'uiaction_accessibility_mode_exit',
    SETTINGS_DIALOG_OPEN = 'uiaction_settings_dialog_open',
    SETTINGS_RESET = 'uiaction_settings_reset',
    BOOKSHELF_DIALOG_OPEN = 'uiaction_bookshelf_dialog_open',
    CLOSE_DIALOGS_AND_FOCUS_ON_READING_POSITION = 'uiaction_close_dialogs_and_focus_on_reading_position',
    GOTO_PAGE_DIALOG_OPEN = 'uiaction_goto_page_dialog_open',
    ZOOM_TOOL_ACTIVATE = 'uiaction_zoom_mode_enter',
    ZOOM_TOOL_DEACTIVATE = 'uiaction_zoom_mode_exit',
    ZOOM_OUT = 'uiaction_zoom_out',
    ZOOM_IN = 'uiaction_zoom_in',
    ZOOM_RESET = 'uiaction_zoom_reset',
    READING_RULER_ACTIVATE = 'uiaction_ruler_activate',
    READING_RULER_DEACTIVATE = 'uiaction_ruler_deactivate',
    READING_RULER_MOVE_NEXT = 'uiaction_ruler_move_next',
    READING_RULER_MOVE_PREVIOUS = 'uiaction_ruler_move_previous',
    LANDMARKS_DIALOG_OPEN = 'uiaction_landmarks_dialog_open',
    NAVIGATION_HISTORY_DIALOG_OPEN = 'uiaction_navigation_history_dialog_open',
}

export interface IVanillaReaderStoreUiReadingRulerOptions {
    overlayIsVisible?: boolean,
    overlayBackgroundColor?: string,
    overlayOpacity?: number,
    overlayMixBlendMode?: StoreCssMixBlendModeValues,

    rulerBackgroundColor?: string,
    rulerOpacity?: number,
    rulerMixBlendMode?: StoreCssMixBlendModeValues,
    rulerMinHeight?: number,

    rulerDragThreshold?: number,

    rulerBorderTopWidth?: number,
    rulerBorderBottomWidth?: number,
    rulerBorderLeftWidth?: number,
    rulerBorderRightWidth?: number,
    rulerBorderTopColor?: string,
    rulerBorderBottomColor?: string,
    rulerBorderLeftColor?: string,
    rulerBorderRightColor?: string,

    lineBoxBackgroundColor?: string,
    lineBoxOpacity?: number,
    lineBoxMixBlendMode?: StoreCssMixBlendModeValues,
    lineBoxLineVerticalSpacing?: number,

    lineBoxBorderTopWidth?: number,
    lineBoxBorderBottomWidth?: number,
    lineBoxBorderLeftWidth?: number,
    lineBoxBorderRightWidth?: number,
    lineBoxBorderTopColor?: string,
    lineBoxBorderBottomColor?: string,
    lineBoxBorderLeftColor?: string,
    lineBoxBorderRightColor?: string,

    lineBoxMinHeight?: number,
    lineBoxMaxHeight?: number,
    lineBoxLineSpan?: number,

    snapToLineBoxes?: boolean,
}

export const enum StoreCssMixBlendModeValues {
    INHERIT = 'inherit',
    INITIAL = 'initial',
    REVERT = 'revert',
    REVERT_LAYER = 'revert-layer',
    UNSET = 'unset',
    NORMAL = 'normal',
    MULTIPLY = 'multiply',
    SCREEN =  'screen',
    OVERLAY = 'overlay',
    DARKEN = 'darken',
    LIGHTEN = 'lighten',
    COLOR_DODGE = 'color-dodge',
    COLOR_BURN = 'color-burn',
    HARD_LIGHT = 'hard-light',
    SOFT_LIGHT = 'soft-light',
    DIFFERENCE = 'difference',
    EXCLUSION = 'exclusion',
    HUE = 'hue',
    SATURATION = 'saturation',
    COLOR = 'color',
    LUMINOSITY = 'luminosity',
}

export interface IVanillaReaderStorePublicationCacheData {
    _id?: string,
    publicationId?: string,
    landmarkData?: IVanillaReaderStorePublicationLandmarkData | undefined,
    storageState?: IReaderPublicationStorageState,
    searchIndexData?: IPublicationSearchIndexDataStore,
    navigationTreeData?: IVanillaReaderStoreNavigationTreeData,
    timelineData?: string | undefined;
}

export interface IVanillaReaderStorePublicationLandmarkData {
    _id?: number,
    publicationId: string,
    documents: IVanillaReaderStoreDocumentLandmarkData[];
}

export interface IVanillaReaderStoreDocumentLandmarkData {
    documentIndex: number,
    headings?: IVanillaReaderStoreLandmarkData[],
    tables?: IVanillaReaderStoreLandmarkData[],
    figures?: IVanillaReaderStoreLandmarkData[],
    audio?: IVanillaReaderStoreLandmarkData[],
    video?: IVanillaReaderStoreLandmarkData[],
    lists?: IVanillaReaderStoreLandmarkData[],
    sections?: IVanillaReaderStoreLandmarkData[],
    articles?: IVanillaReaderStoreLandmarkData[],
    asides?: IVanillaReaderStoreLandmarkData[],
    footnotes?: IVanillaReaderStoreLandmarkData[];
}

export interface IVanillaReaderStoreLandmarkData extends IContentBlockData {
    timelinePosition: number | undefined,
    subType?: VanillaReaderPublicationLandmarkType,
    description?: string,
    locatorUrl?: string,
}

export interface IPublicationSearchIndexDataStore {
    hashSignature: string,
    metadata: IPublicationMetadataItem[],
    documents: IPublicationSearchIndexDocumentDataStore[],
    publicationIndexedTextLength: number;
}

export interface IPublicationSearchIndexDocumentDataStore {
    tocTitle: string | undefined,
    locator: string,
    contentNodes: IPublicationSearchIndexContentNodeDataStore[],
    indexedText?: string,
    charOffsetInPublicationIndexedText: number,
    indexInPublicationSpine: number;
}

export interface IPublicationSearchIndexContentNodeDataStore {
    tocTitle: string | undefined,
    id: number,
    locator: string,
    charRangeInIndexedDocumentText?: IntRange | undefined,
    type: ContainerContentBlockType | MediaContentBlockType | TextContentBlockType,
    attributes: IAttributeData[],
    documentIndex: number;
}

export interface IVanillaReaderStoreNavigationTreeData {
    collections: IVanillaReaderStoreNavigationCollection[],
    items: IVanillaReaderStoreNavigationItem[];
}

export interface IVanillaReaderStoreNavigationCollection {
    type: NavigationCollectionType,
    children: IVanillaReaderStoreNavigationItem[];
}

export interface IVanillaReaderStoreNavigationItem {
    title: string,
    collectionType?: NavigationCollectionType,
    locator?: string,
    timelineStartPosition?: number,
    children: IVanillaReaderStoreNavigationItem[];
}

export interface IVanillaReaderStorePublicationOptionsData {
    _id?: string,
    publicationId: string,
    styleOptions?: IPublicationStyleOptions,
    ttsOptions?: IVanillaReaderStoreTtsOptions;
}

export interface IVanillaReaderStorePublicationData {
    id: string,
    fileName: string,
    fileSourceUri: string,
    format: string,
    url: string,
    title?: string,
    isRtl?: boolean,
    isFixedLayout?: boolean,
    isScripted?: boolean,
    isAudiobook?: boolean,
    hasMoSyncMedia?: boolean,
    metadata?: IPublicationMetadataData,
    coverImageAsBase64?: string,
    language?: string;
}

export interface IVanillaReaderStoreContentPositionData {
    _id?: string | undefined,
    publicationId?: string | undefined,
    contentBlockData?: IContentBlockData,
    contentRects?: IVisibleContentRectData[],
    locator: string | undefined,
    navItemData?: IVanillaReaderStoreNavigationItem[],
    documentIndex?: number,
    timestamp: number;
}

export enum VanillaReaderLocalStorageKeys {
    VanillaReaderUiActiveOptionsPreset = 'VanillaReaderUiActiveOptionsPreset',
    VanillaReaderUserProfileData = 'VanillaReaderUserProfileData',
    VanillaReaderAppDataMigrationDateStamp = 'VanillaReaderAppDataMigrationDateStamp',
    VanillaReaderActiveColorPalette = 'VanillaReaderActiveColorPalette',
    VanillaReaderViewInitialOptions = 'VanillaReaderViewInitialOptions',
}
